// lib/nwcUtils.ts
import { CategoryOption, NWCRole } from '../types';

/**
 * Detects appropriate NWC role based on category properties
 */
export const detectNWCRole = (category: CategoryOption): NWCRole => {
  const { value, type, label } = category;
  
  // Only current assets and liabilities can have NWC roles
  if (type !== 'current-asset' && type !== 'current-liability') {
    return null;
  }
  
  // Common terms that indicate specific NWC roles
  const lowerValue = value.toLowerCase();
  const lowerLabel = label.toLowerCase();
  
  // Cash detection
  if (
    type === 'current-asset' && 
    (lowerValue.includes('cash') || 
     lowerLabel.includes('cash') ||
     lowerValue.includes('money-market') ||
     lowerLabel.includes('money market'))
  ) {
    return 'cash';
  }
  
  // Accounts Receivable detection
  if (
    type === 'current-asset' && 
    (lowerValue.includes('receivable') || 
     lowerLabel.includes('receivable') ||
     lowerValue.includes('ar') ||
     lowerLabel.includes('a/r'))
  ) {
    return 'accounts-receivable';
  }
  
  // Inventory detection
  if (
    type === 'current-asset' && 
    (lowerValue.includes('inventory') || 
     lowerLabel.includes('inventory') ||
     lowerValue.includes('stock') ||
     lowerLabel.includes('stock'))
  ) {
    return 'inventory';
  }
  
  // Accounts Payable detection
  if (
    type === 'current-liability' && 
    (lowerValue.includes('payable') || 
     lowerLabel.includes('payable') ||
     lowerValue.includes('ap') ||
     lowerLabel.includes('a/p'))
  ) {
    return 'accounts-payable';
  }
  
  // Short-term Debt detection
  if (
    type === 'current-liability' && 
    (lowerValue.includes('debt') || 
     lowerLabel.includes('debt') ||
     lowerValue.includes('loan') ||
     lowerLabel.includes('loan') ||
     lowerValue.includes('borrowing') ||
     lowerLabel.includes('borrowing') ||
     lowerValue.includes('note') ||
     lowerLabel.includes('note'))
  ) {
    return 'short-term-debt';
  }
  
  // Right-of-Use (debt-like) detection
  if (
    (lowerValue.includes('right-of-use') || 
     lowerLabel.includes('right of use') ||
     lowerValue.includes('rou') ||
     lowerLabel.includes('rou') ||
     lowerValue.includes('lease') ||
     lowerLabel.includes('lease'))
  ) {
    return 'debt-like';
  }
  
  return null;
};

/**
 * Determines if a role represents a definitional adjustment
 */
export const isDefinitionalAdjustment = (role: NWCRole): boolean => {
  return role === 'cash' || role === 'short-term-debt' || role === 'debt-like';
};

/**
 * Determines if a role represents a trade working capital component
 */
export const isTradeWorkingCapital = (role: NWCRole): boolean => {
  return role === 'inventory' || role === 'accounts-receivable' || role === 'accounts-payable';
};

/**
 * Gets appropriate CSS classes for displaying NWC role
 */
export const getNWCRoleColorClass = (role: NWCRole): string => {
  if (isDefinitionalAdjustment(role)) {
    return 'bg-amber-100 text-amber-800'; // Amber for definitional adjustments
  }
  
  if (isTradeWorkingCapital(role)) {
    return 'bg-blue-100 text-blue-800';   // Blue for trade working capital
  }
  
  return 'bg-gray-100 text-gray-800';     // Gray for other or unspecified
};

/**
 * Gets background color class for table rows based on NWC role
 */
export const getNWCRowColorClass = (role: NWCRole): string => {
  if (isDefinitionalAdjustment(role)) {
    return 'bg-amber-50'; 
  }
  
  if (isTradeWorkingCapital(role)) {
    return 'bg-blue-50';
  }
  
  return '';
};

/**
 * Gets human-readable description for NWC role
 */
export const getNWCRoleDescription = (role: NWCRole): string => {
  switch (role) {
    case 'cash':
      return 'Cash (Definitional Adjustment)';
    case 'short-term-debt':
      return 'Short-term Debt (Definitional Adjustment)';
    case 'debt-like':
      return 'Debt-like Item (Definitional Adjustment)';
    case 'accounts-receivable':
      return 'Accounts Receivable (Trade WC)';
    case 'inventory':
      return 'Inventory (Trade WC)';
    case 'accounts-payable':
      return 'Accounts Payable (Trade WC)';
    default:
      return 'Not Specified';
  }
};

/**
 * Formats NWC role for display
 */
export const formatNWCRole = (role: NWCRole): string => {
  if (!role) return '';
  
  return role
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
